import type { AtlasManifest } from '@atlas-manifest/types';
import type { EditableKind } from './aiPrompts.js';
import { entityById } from './selectors.js';

/**
 * Realistic-value patterns worth flagging before they're sent to an external
 * AI provider — not a PII taxonomy, just "this looks like it could be a real
 * instance value, not a placeholder." Deliberately conservative (SSN and
 * card-number shapes only) to keep false positives low; see Task 2 of the
 * 2026-08-01 Atlas review roadmap for why this is a caution, not a scrubber.
 */
const PATTERNS: ReadonlyArray<RegExp> = [
  /\b\d{3}-\d{2}-\d{4}\b/, // SSN-shaped
  /\b(?:\d[ -]?){13,19}\b/, // card-number-shaped
];

const SENSITIVE_CATEGORIES = new Set(['DIRECT', 'FINANCIAL']);

function collectStrings(obj: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const value of Object.values(obj)) {
    if (typeof value === 'string') out.push(value);
    else if (Array.isArray(value)) {
      for (const v of value) if (typeof v === 'string') out.push(v);
    }
  }
  return out;
}

/**
 * Caution-only (never blocks) check for realistic-looking values in an
 * object about to be sent to Gemini as part of an edit proposal, scoped to
 * entities whose piiCategory marks them as handling sensitive data. Mirrors
 * checkEditProposalDomainReference's contract: null = no concern, string =
 * caution text for the same non-blocking UI slot.
 */
export function checkPiiLikeContent(
  currentObject: Record<string, unknown>,
  kind: EditableKind,
  selectorId: string,
  manifest: AtlasManifest,
): string | null {
  if (kind !== 'entity') return null;
  const entity = entityById(manifest, selectorId);
  if (!entity?.piiCategory || !SENSITIVE_CATEGORIES.has(entity.piiCategory)) return null;

  for (const text of collectStrings(currentObject)) {
    for (const pattern of PATTERNS) {
      if (pattern.test(text)) {
        return `This ${entity.piiCategory.toLowerCase()}-data entity's content looks like a real value, not a placeholder — double-check before sending to Atlas, and before accepting its answer.`;
      }
    }
  }
  return null;
}
